// The arena (gate B4, `?scene=arena`): a walled patch of the valley's ground with pillars, low walls and rocks, the
// hero on @rpg/controller (driven by the player or a bot), and the fight (./fight.ts): @rpg/combat for the hero and
// the enemies, the enemies on @rpg/nav's crowd with @rpg/ai's brains (perception, behaviour trees as data, one
// attack-token pool on the hero, the encounter director's waves), and the bodies module's poses, reactions and
// ragdolls. All of it runs in the spike's one kernel game, on the 120 Hz step:
//   fight:act      (order -10)  combat's step begins; the hero's intent; a boss's phase; the director; the brains
//   arena:hero     (order 0)    the hero's controller (the player's stick or a bot's input; combat's drive)
//   fight:move     (order 50)   the crowd steps, the hurt sensors follow everyone
//   arena:physics  (order 100)  the Rapier step (24 solver iterations: the ragdolls)
//   bodies         (order 110)  the ragdolls
//   fight:resolve  (order 150)  hit volumes sweep, contacts resolve, events go out
// State: the hero's controller (a component) and the fight's components: saved and hashed; the crowd rides the
// fight's save slice and comes back bit for bit (@rpg/nav re-seats Detour every step).
import type { GameModule, World } from "@rpg/kernel";
import type { ActionMap, Actions, RngStreams } from "@rpg/runtime";
import type { BodySpec, CollisionWorld, LayerTable } from "@rpg/collision";
import { NO_INPUT, airMode, createController, groundMode, stickToWorld, type Controller, type ControllerInput, type ControllerState } from "@rpg/controller";
import { buildNavWorld, navGeometry, type NavShape, type NavWorld } from "@rpg/nav";
import type { Intent } from "@rpg/combat";
import type { GameEvents } from "../core";
import type { SceneDef, SceneSetup } from "../sim/host";
import { terrainHeight } from "../valley/course";
import type { ValleyTuning } from "../valley/tuning";
import type { Vec3 } from "./brains";
import { FIGHT_FAULTS, FIGHT_LAYERS, FIGHT_SOLVER_ITERATIONS, createFight, type Fight, type FightContent, type FightState } from "./fight";

export const ARENA_LAYERS: LayerTable = { terrain: [], solid: [], hero: ["terrain", "solid"], ...FIGHT_LAYERS };
export const ARENA_SEED = 0xa2e7a;
export const HERO_C = "arena/Hero";
export const PHYSICAL_C = "sim/Physical";
/** The arena camera's fixed yaw: the stick is relative to it (the view looks toward -z). */
export const ARENA_CAMERA_YAW = Math.PI;
/** The pen: ±HALF m of ground, walls at ±WALL. */
export const HALF = 20;
const CELLS = 40;
const WALL = 19.6;
/** Where the hero starts and respawns. */
export const HERO_SPAWN: Vec3 = [0, 0, 4];

/** The arena's own action map, used only where the game's input isn't passed in (Node runs). */
export const ARENA_ACTIONS: ActionMap = {
  buttons: { jump: ["Space"], sprint: ["ShiftLeft"], attack: ["KeyJ"], dodge: ["KeyK"], block: ["KeyL"], skill: ["KeyU"], bigMove: ["KeyI"], weaponSwitch: ["KeyO"] },
  sticks: { move: { up: ["KeyW"], down: ["KeyS"], left: ["KeyA"], right: ["KeyD"], vectors: ["PadLeft"] } },
};

/** The arena's planted defects are the fight's (the token cap, the crowd's re-seat). */
export const ARENA_FAULTS = FIGHT_FAULTS;
export type ArenaFaultName = keyof typeof ARENA_FAULTS;

/** What the arena exposes to its handle, bots and view. */
export interface ArenaRig {
  collision: CollisionWorld;
  actions: Actions;
  nav: NavWorld;
  fight: Fight;
  content: FightContent;
  tuning: ValleyTuning;
  heroCtl: Controller;
  /** The fight's state (enemies, tokens, director, the hero's respawn). */
  state(): FightState;
  hero(): ControllerState;
  dt: number;
  /** Called at the start of each sim step, before the tick (bots steer here: set `heroInput` and `heroIntent`). */
  drive: (() => void) | null;
  /** The hero's input for this step instead of the actions (a bot's), and a combat intent to request. */
  heroInput: ControllerInput | null;
  heroIntent: Intent | null;
  /** Called after each sim step's tick. */
  observe: (() => void) | null;
  terrainAt(x: number, z: number): number;
  warpHero(x: number, z: number, yaw?: number): void;
}

const quatY = (yaw: number) => ({ x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) });

/** The arena's static layout: ground, walls, pillars, low walls and rocks, as collider specs and nav shapes. */
export function arenaLayout(): { bodies: BodySpec[]; nav: NavShape[] } {
  const heights: number[] = [];
  for (let iz = 0; iz <= CELLS; iz++) for (let ix = 0; ix <= CELLS; ix++) heights.push(terrainHeight((ix / CELLS - 0.5) * 2 * HALF, (iz / CELLS - 0.5) * 2 * HALF));
  const bodies: BodySpec[] = [{ id: "terrain", kind: "fixed", colliders: [{ shape: { type: "heightfield", rows: CELLS, cols: CELLS, heights, size: { x: 2 * HALF, y: 1, z: 2 * HALF } }, layer: "terrain", tag: "grass" }] }];
  const nav: NavShape[] = [{ kind: "heightfield", rows: CELLS, cols: CELLS, heights, size: [2 * HALF, 1, 2 * HALF] }];
  const box = (id: string, c: Vec3, h: Vec3, yaw = 0, tag = "stone"): void => {
    const rotation = quatY(yaw);
    bodies.push({ id, kind: "fixed", position: { x: c[0], y: c[1], z: c[2] }, rotation, colliders: [{ shape: { type: "cuboid", halfExtents: { x: h[0], y: h[1], z: h[2] } }, layer: "solid", tag }] });
    nav.push({ kind: "box", center: c, halfExtents: h, rotation });
  };
  for (const [id, x, z, hx, hz] of [
    ["wall-n", 0, -WALL, WALL, 0.4],
    ["wall-s", 0, WALL, WALL, 0.4],
    ["wall-w", -WALL, 0, 0.4, WALL],
    ["wall-e", WALL, 0, 0.4, WALL],
  ] as const)
    box(id, [x, 1.5, z], [hx, 1.5, hz]);
  // pillars: tall cylinders, cover from sight and something to circle
  for (const [i, [x, z]] of [
    [-8, -6],
    [8, -6],
    [-8, 6],
    [8, 6],
  ].entries()) {
    const y = terrainHeight(x!, z!) + 1.5;
    bodies.push({ id: `pillar-${i}`, kind: "fixed", position: { x: x!, y, z: z! }, colliders: [{ shape: { type: "cylinder", halfHeight: 1.6, radius: 0.7 }, layer: "solid", tag: "stone" }] });
    nav.push({ kind: "cylinder", center: [x!, y, z!], radius: 0.7, halfHeight: 1.6, segments: 10 });
  }
  // low walls (waist high: block a path, not a shade's sight of the hero's head) and rocks
  box("low-wall-s", [0, terrainHeight(0, 9) + 0.55, 9], [2.6, 0.6, 0.35]);
  box("low-wall-w", [-12, terrainHeight(-12, -3) + 0.55, -3], [2.4, 0.6, 0.35], Math.PI / 2);
  box("rock-ne", [11, terrainHeight(11, -11) + 0.4, -11], [1.3, 0.8, 1], 0.5);
  box("rock-se", [12, terrainHeight(12, 12) + 0.4, 12], [1, 0.9, 1.3], -0.4);
  return { bodies, nav };
}



/** The arena's options: the difficulty, the director on at the start, the runtime clock's slow-mo (combat's). */
export interface ArenaOptions {
  difficulty?: string;
  director?: boolean;
  slowMo?(scale: number, seconds: number, easeBack: number): void;
}

/** The navmesh's build settings for the arena (the game's numbers: an agent of the common enemy size). */
export const ARENA_NAV = { cellSize: 0.25, cellHeight: 0.1, tileSize: 32, agentRadius: 0.4, agentHeight: 1.6, agentClimb: 0.35, maxSlope: 40, bounds: [[-HALF, -2, -HALF], [HALF, 5, HALF]] as [Vec3, Vec3] };

/** Build the arena scene. `rig()` is valid once the host has called `setup`. */
export function createArena(tuning: ValleyTuning, content: FightContent, o: ArenaOptions = {}): { def: SceneDef; rig(): ArenaRig } {
  let rig: ArenaRig | null = null;
  const def: SceneDef = {
    name: "arena",
    layers: ARENA_LAYERS,
    seed: ARENA_SEED,
    actions: ARENA_ACTIONS,
    solverIterations: FIGHT_SOLVER_ITERATIONS,
    setup(collision, rng, actions) {
      const s = setupArena(collision, actions, rng, tuning, content, o);
      rig = s.rig;
      return s.setup;
    },
  };
  return {
    def,
    rig: () => {
      if (!rig) throw new Error("arena: not set up yet");
      return rig;
    },
  };
}

function setupArena(collision: CollisionWorld, actions: Actions, rng: RngStreams, tuning: ValleyTuning, content: FightContent, opts: ArenaOptions): { setup: SceneSetup; rig: ArenaRig } {
  const layout = arenaLayout();
  const nav = buildNavWorld(navGeometry(layout.nav), ARENA_NAV);
  let world: World | null = null;
  const hero = (): ControllerState => {
    const e = world!.query(HERO_C)[0];
    if (e === undefined) throw new Error("arena: no hero");
    return world!.getComponent<ControllerState>(e, HERO_C)!;
  };
  const terrainAt = (x: number, z: number): number => collision.castRay({ x, y: 100, z }, { x: 0, y: -1, z: 0 }, 200, { layers: ["terrain"] })?.point.y ?? terrainHeight(x, z);
  let heroCtl: Controller = null as unknown as Controller; // made in the content build, once the hero's body exists

  const fight = createFight({
    collision,
    actions,
    rng,
    nav,
    content,
    slowMo: (scale, s, ease) => opts.slowMo?.(scale, s, ease),
    hero,
    heroCtl: () => heroCtl,
    cameraYaw: () => ARENA_CAMERA_YAW,
    respawnAt: () => ({ x: HERO_SPAWN[0], z: HERO_SPAWN[2], yaw: Math.PI }),
    terrainAt,
    encounter: "arena",
    director: opts.director ?? true,
    difficulty: opts.difficulty ?? "normal",
  });

  const t = tuning.hero;
  const heroBody: BodySpec = {
    id: "hero",
    kind: "kinematic",
    position: { x: HERO_SPAWN[0], y: t.halfHeight + t.radius, z: HERO_SPAWN[2] },
    colliders: [{ shape: { type: "capsule", halfHeight: t.halfHeight, radius: t.radius }, layer: "hero" }],
  };
  const bodies = [...layout.bodies, heroBody];

  const module: GameModule<GameEvents> = {
    id: "arena",
    version: "0.2.0",
    dependencies: ["fight"],
    setup(ctx) {
      ctx.registerSystem(
        ({ dt: stepDt }) => {
          const h = hero();
          let input: ControllerInput = NO_INPUT;
          if (rigObj.heroInput) input = rigObj.heroInput;
          else {
            const stick = actions.stick("move");
            input = { move: stickToWorld(stick, ARENA_CAMERA_YAW), jump: actions.down("jump"), jumpPressed: actions.pressed("jump"), sprint: actions.down("sprint"), glide: false, glidePressed: false };
          }
          // combat's move override (a lunge, a roll, rooted while acting or down, a knockback) replaces the stick
          const drive = fight.heroDrive();
          heroCtl.step(h, drive ? { ...input, jumpPressed: false, drive } : input, stepDt);
        },
        { name: "arena:hero", order: 0 },
      );
      ctx.registerSystem(() => collision.step(), { name: "arena:physics", order: 100 });
      ctx.registerSaveSlice({
        serialize: () => ({ hero: structuredClone(hero()) }),
        deserialize: (data) => void Object.assign(hero(), structuredClone((data as { hero: ControllerState }).hero)),
      });
    },
  };

  const pack = {
    meta: { id: "arena", displayName: "The arena", version: "0.2.0" },
    requiresModules: [{ id: "arena", versionRange: "^0.2.0" }],
    build(c: { world: World }) {
      world = c.world;
      for (const b of bodies) {
        collision.add(b);
        world.addComponent(world.createEntity(), PHYSICAL_C, { body: b.id });
      }
      collision.step();
      heroCtl = createController({ world: collision, body: "hero", tuning: tuning.hero, modes: [groundMode(), airMode()], surfaces: tuning.surfaces });
      const hs = heroCtl.spawn({ x: HERO_SPAWN[0], y: terrainHeight(HERO_SPAWN[0], HERO_SPAWN[2]), z: HERO_SPAWN[2] }, Math.PI);
      world.addComponent(world.createEntity(), HERO_C, hs);
      fight.build(world);
    },
  };

  const rigObj: ArenaRig = {
    collision,
    actions,
    nav,
    fight,
    content,
    tuning,
    get heroCtl() {
      return heroCtl;
    },
    state: () => fight.state(),
    hero,
    dt: collision.dt,
    drive: null,
    heroInput: null,
    get heroIntent() {
      return fight.heroIntent;
    },
    set heroIntent(i: Intent | null) {
      fight.heroIntent = i;
    },
    observe: null,
    terrainAt,
    warpHero(x, z, yaw = hero().yaw) {
      heroCtl.warp(hero(), { x, y: terrainAt(x, z), z }, yaw);
    },
  };

  const setup: SceneSetup = {
    parts: { modules: [...fight.modules, module], content: pack as SceneSetup["parts"]["content"] },
    hashed: [HERO_C, PHYSICAL_C, ...fight.hashed],
    bodies,
    beforeTick() {
      // a bot steers before the step, so its intent is taken in the same step
      rigObj.drive?.();
      fight.botDriving = rigObj.heroInput !== null;
    },
    afterTick() {
      rigObj.observe?.();
    },
  };
  return { setup, rig: rigObj };
}
